import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileMagnifyingGlass as FileSearch, Globe, Coin, Users, FileText, ShieldCheck, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { listCmcAudit, getSocialUtilityIndicators } from '../../services/cmcApi';
import { CmcPageHeader, CmcEmpty, fmtAmount, fmtDate } from '../../components/cmc/CmcCommon';

const ENTITY_TYPES = [
  { value: 'all', label: 'Tous types' },
  { value: 'cooperator', label: 'Coopérateur' },
  { value: 'ucc', label: 'UCC' },
  { value: 'rule', label: 'Règle' },
  { value: 'contract', label: 'Contrat' },
  { value: 'payment', label: 'Paiement' },
  { value: 'attestation', label: 'Attestation' },
];

function FamilyCard({ family, amount }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#F0EFEB] last:border-0">
      <span className="text-xs text-[#5E6D66]">{family}</span>
      <span className="text-sm font-semibold text-[#1A2421]">{fmtAmount(amount)}</span>
    </div>
  );
}

export default function CmcAuditPage() {
  const [audit, setAudit] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [entityIdFilter, setEntityIdFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entityFilter !== 'all') params.entity_type = entityFilter;
      if (entityIdFilter) params.entity_id = entityIdFilter;
      const [a, ind] = await Promise.all([listCmcAudit(params), getSocialUtilityIndicators()]);
      setAudit(a.data.items || []);
      setIndicators(ind.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [entityFilter, entityIdFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const computed = indicators?.computed || {};

  return (
    <div data-testid="cmc-audit-page">
      <CmcPageHeader
        title="Audit & Utilité sociale"
        subtitle="Journal des événements et indicateurs d'utilité sociale CMC"
        actions={
          <Button
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            onClick={() => {
              const rows = (audit || []).map(a => ({
                date: a.timestamp || '', actor: a.actor_email || '', action: a.action_type || '', target: a.target_type || '',
              }));
              const csv = "\ufeffDate;Auteur;Action;Cible\n" + rows.map(r => `${r.date};${r.actor};${r.action};${r.target}`).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `audit-cmc-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
              URL.revokeObjectURL(url);
            }}
            data-testid="audit-export-csv"
          >
            <DownloadSimple size={16} className="mr-2" /> Exporter le journal
          </Button>
        }
      />

      {/* Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold mb-2 flex items-center gap-1.5">
              <Globe size={14} className="text-[#2D5A4C]" /> Utilité sociale calculée
            </div>
            <div className="space-y-3 mt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5E6D66] flex items-center gap-1.5"><Users size={12} /> Coopérateurs</span>
                <span className="text-sm font-semibold text-[#1A2421]">{computed.cooperators_total ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5E6D66] flex items-center gap-1.5"><Coin size={12} /> UCC</span>
                <span className="text-sm font-semibold text-[#1A2421]">{computed.ucc_total ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5E6D66] flex items-center gap-1.5"><FileText size={12} /> Paiements versés</span>
                <span className="text-sm font-semibold text-[#1A2421]">{fmtAmount(computed.payments_paid_total_amount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5E6D66] flex items-center gap-1.5"><ShieldCheck size={12} /> Attestations émises</span>
                <span className="text-sm font-semibold text-[#1A2421]">{computed.attestations_issued ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#5E6D66]">Affectations coop. totales</span>
                <span className="text-sm font-semibold text-[#1A2421]">{fmtAmount(computed.affectations_cooperatives_total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#E8E6E1] md:col-span-2">
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#5E6D66] font-semibold mb-3">Affectations par famille</div>
            {Object.keys(computed.affectations_by_family || {}).length === 0 ? (
              <div className="text-xs text-[#5E6D66] py-2">Aucune affectation enregistrée.</div>
            ) : (
              Object.entries(computed.affectations_by_family).map(([fam, amt]) => (
                <FamilyCard key={fam} family={fam} amount={amt} />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Audit filters */}
      <Card className="border-[#E8E6E1] mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="min-w-[200px]">
            <Label className="text-xs text-[#5E6D66]">Type d'entité</Label>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger data-testid="audit-filter-entity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-[#5E6D66]">ID d'entité (optionnel)</Label>
            <Input data-testid="audit-filter-entity-id" placeholder="UUID" value={entityIdFilter} onChange={(e) => setEntityIdFilter(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {/* Audit table */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#2D5A4C]" /></div>
          ) : audit.length === 0 ? (
            <CmcEmpty icon={FileSearch} message="Aucun événement d'audit" hint="Les actions sur les entités CMC apparaîtront ici." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F5] text-[11px] uppercase tracking-wide text-[#5E6D66]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Date</th>
                    <th className="text-left px-4 py-2.5 font-medium">Action</th>
                    <th className="text-left px-4 py-2.5 font-medium">Entité</th>
                    <th className="text-left px-4 py-2.5 font-medium">Référence</th>
                    <th className="text-left px-4 py-2.5 font-medium">Acteur</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEB]">
                  {audit.map((ev) => (
                    <tr key={ev.id} className="hover:bg-[#FAFAF8]" data-testid={`audit-event-${ev.id}`}>
                      <td className="px-4 py-3 text-xs text-[#5E6D66] whitespace-nowrap">{fmtDate(ev.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-[#1A2421] capitalize">{ev.action}</td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{ev.entity_type}</td>
                      <td className="px-4 py-3 text-xs">
                        {ev.entity_label || <span className="font-mono text-[#5E6D66]">{ev.entity_id?.slice(0, 8)}…</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5E6D66]">{ev.user_email || ev.user_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
